import { Button } from 'primereact/button';
import { Menu as PrimeMenu } from 'primereact/menu';
import React, { useRef } from 'react';

import { useMindmap } from '../use-mindmap';
import { CopyCutPasteActions } from '../use-mindmap-copy-cut-paste';
import { Menu, MenuVariantElementProps } from './use-tool-bar';

const buttonClasses = 'p-button-text';

const copyCutPasteMenuLarge = ({ hook }: MenuVariantElementProps) => {
  const values = hook();
  if (values?.type == 'node-cut-copy-paste') {
    const {
      actions: {
        onCopyClick,
        onPasteClick,
        onCutClick,
        onUncutClick,
        copySelectable,
        pasteSelectable,
        cutSelectable,
        uncutSelectable,
      },
    } = values;

    return (
      <>
        <div style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
          <Button
            onClick={onCopyClick}
            label="Copy"
            icon="pi ri-clipboard-line"
            className={buttonClasses}
            disabled={!copySelectable}
          />
        </div>
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }}>
          <Button
            onClick={onCutClick}
            label="Cut"
            icon="pi ri-scissors-cut-line"
            className={buttonClasses}
            disabled={!cutSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
          <Button
            onClick={onPasteClick}
            label="Paste"
            icon="pi ri-file-copy-line"
            className={buttonClasses}
            disabled={!pasteSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '2 / 3' }}>
          <Button
            onClick={onUncutClick}
            label="Uncut"
            icon="pi ri-arrow-go-back-line"
            className={buttonClasses}
            disabled={!uncutSelectable}
          />
        </div>
      </>
    );
  }
  throw new Error('Unknown or missing hook or hook type.');
};

const copyCutPasteMenuMedium = ({ hook }: MenuVariantElementProps) => {
  const values = hook();
  if (values?.type == 'node-cut-copy-paste') {
    const {
      actions: {
        onCopyClick,
        onPasteClick,
        onCutClick,
        onUncutClick,
        copySelectable,
        pasteSelectable,
        cutSelectable,
        uncutSelectable,
      },
    } = values;

    return (
      <>
        <div style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
          <Button
            onClick={onCopyClick}
            icon="pi ri-clipboard-line"
            className={buttonClasses}
            disabled={!copySelectable}
          />
        </div>
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }}>
          <Button
            onClick={onCutClick}
            icon="pi ri-scissors-cut-line"
            className={buttonClasses}
            disabled={!cutSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
          <Button
            onClick={onPasteClick}
            icon="pi ri-file-copy-line"
            className={buttonClasses}
            disabled={!pasteSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '2 / 3' }}>
          <Button
            onClick={onUncutClick}
            icon="pi ri-arrow-go-back-line"
            className={buttonClasses}
            disabled={!uncutSelectable}
          />
        </div>
      </>
    );
  }
  throw new Error('Unknown or missing hook or hook type.');
};

const copyCutPasteMenuSmall = ({ hook }: MenuVariantElementProps) => {
  const values = hook();
  if (values?.type == 'node-cut-copy-paste') {
    const {
      actions: {
        onCopyClick,
        onPasteClick,
        onCutClick,
        onUncutClick,
        copySelectable,
        pasteSelectable,
        cutSelectable,
        uncutSelectable,
      },
      menuRef,
    } = values;

    const items = [
      { label: 'Copy', command: onCopyClick, icon: 'pi ri-clipboard-line', disabled: !copySelectable },
      { label: 'Cut', command: onCutClick, icon: 'pi ri-scissors-cut-line', disabled: !cutSelectable },
      { label: 'Paste', command: onPasteClick, icon: 'pi ri-file-copy-line', disabled: !pasteSelectable },
      { label: 'Uncut', command: onUncutClick, icon: 'pi ri-arrow-go-back-line', disabled: !uncutSelectable },
    ];

    const onMenuClick = (event: any) => {
      // @ts-ignore
      menuRef.current.toggle(event);
    };

    const allDisabled = !copySelectable && !cutSelectable && !pasteSelectable && !uncutSelectable;

    return (
      <>
        <div style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
          <PrimeMenu model={items} ref={menuRef} popup id="node_copy-cut-paste_popup_menu" />
          <Button
            icon="pi ri-file-copy-2-line"
            className={buttonClasses}
            disabled={allDisabled}
            onClick={onMenuClick}
            aria-controls="node_copy-cut-paste_popup_menu"
            aria-haspopup
          />
        </div>
      </>
    );
  }
  throw new Error('Unknown or missing hook or hook type.');
};

export interface NodeCutCopyPasteHookValue {
  type: 'node-cut-copy-paste';
  actions: CopyCutPasteActions;
  menuRef: React.MutableRefObject<null>;
}

export const nodeCutCopyPasteMenu: Menu = {
  id: 'menu-copy-cut-paste',
  variants: [
    { Element: copyCutPasteMenuSmall, priority: 5 },
    { Element: copyCutPasteMenuMedium, priority: 5 },
    { Element: copyCutPasteMenuLarge, priority: 5 },
  ],
  currentVariant: 2,
  hook: () => {
    const {
      copyCutPaste: { actions },
    } = useMindmap();
    const menuRef = useRef(null);

    return {
      type: 'node-cut-copy-paste',
      actions,
      menuRef,
    };
  },
  tempHook: () => ({
    type: 'node-cut-copy-paste',
    actions: {
      onCopyClick: () => {},
      onPasteClick: () => {},
      onCutClick: () => {},
      onUncutClick: () => {},
      copySelectable: false,
      pasteSelectable: false,
      cutSelectable: false,
      uncutSelectable: false,
    },
    menuRef: React.createRef(),
  }),
};
